import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Skeleton,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Title, useRedirect } from 'react-admin';
import { useParams } from 'react-router-dom';
import { DocumentType } from '../../../../interfaces/document';
import referralService from '../../../../services/referral';
import { formatDateString } from '../../../../util/dateFormat';
import { CustomTabs } from '../../../common/CustomTabs/CustomTabs';
import { PageHeader } from '../../../common/PageHeader/PageHeader';
import { TabPanel } from '../../../common/TabPanel/TabPanel';
import '../../../common/tab-section.css';
import { parseDocumentRules } from '../../shared';

const DocumentTypeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [documentType, setDocumentType] = useState<DocumentType | null>(null);
  const [loading, setLoading] = useState(false);
  const [mainTabIndex, setMainTabIndex] = useState(0);
  const redirect = useRedirect();

  useEffect(() => {
    const fetchDocumentType = async () => {
      if (!id) {
        return;
      }

      setLoading(true);
      try {
        const data = await referralService.fetchDocumentType(parseInt(id));
        if (data) {
          setDocumentType(data);
        }
      } catch (error) {
        console.error('Error fetching document type:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDocumentType();
  }, [id]);

  const handleMainTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setMainTabIndex(newValue);
  };

  if (loading || !documentType) {
    return (
      <Box mt={4}>
        <Box className='tab-section'>
          {/* Header Skeleton */}
          <Box sx={{ mb: 3 }}>
            <Skeleton variant='text' width={200} height={32} sx={{ mb: 1 }} />
            <Skeleton variant='text' width={300} height={20} />
          </Box>

          {/* Header Action */}
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Skeleton variant='text' width={250} height={32} sx={{ mb: 1 }} />
              <Skeleton variant='text' width={350} height={20} />
            </Box>
          </Box>

          {/* Tab Skeletons */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
              <Skeleton variant='text' width={130} height={24} />
              <Skeleton variant='text' width={140} height={24} />
            </Box>
          </Box>

          {/* Content Cards */}
          {[1, 2].map((item) => (
            <Card key={item} sx={{ mb: 2 }}>
              <CardContent>
                <Skeleton variant='text' width='40%' height={24} sx={{ mb: 2 }} />
                <Grid container spacing={4}>
                  {[1, 2, 3].map((field) => (
                    <Grid item xs={12} md={4} key={field}>
                      <Skeleton variant='text' width='60%' height={16} sx={{ mb: 1 }} />
                      <Skeleton variant='text' width='80%' height={20} />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          ))}

          {/* Loading indicator */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant='body2' color='text.secondary' sx={{ fontSize: '12px' }}>
              Loading document type details...
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  const parsedRules = parseDocumentRules(documentType.documentRules);

  return (
    <Box mt={4} className='tab-section-table'>
      <Title title={`Document Type: ${documentType.name}`} />

      <CustomTabs
        tabLabels={[
          { label: 'Basic Information', onClick: () => {} },
          ...(documentType?.documentRules ? [{ label: 'Document Rules', onClick: () => {} }] : []),
        ]}
        value={mainTabIndex}
        onChange={handleMainTabChange}
      />

      <TabPanel value={mainTabIndex} index={0} sx={{ padding: '14px 0' }}>
        <Box className='tab-section'>
          <PageHeader
            breadcrumbs={[
              { label: 'Documents', onClick: () => redirect('/documents/library') },
              { label: 'Settings', onClick: () => redirect('/documents/settings') },
              { label: 'Document Types', onClick: () => redirect('/documents/settings') },
              { label: `${documentType.name}`, active: true },
            ]}
          />
          <Card sx={{ boxShadow: 1 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant='h6' gutterBottom sx={{ mb: 3 }}>
                Document Type Information
              </Typography>

              <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                  <Typography variant='body2' color='text.secondary' gutterBottom>
                    Document Type ID
                  </Typography>
                  <Typography variant='body1' fontWeight='medium'>
                    {documentType.id}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant='body2' color='text.secondary' gutterBottom>
                    Name
                  </Typography>
                  <Typography variant='body1' fontWeight='medium'>
                    {documentType.name}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant='body2' color='text.secondary' gutterBottom>
                    Created
                  </Typography>
                  <Typography variant='body1'>
                    {formatDateString(documentType.createdAt, 'MMM dd, yyyy HH:mm:ss')}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant='body2' color='text.secondary' gutterBottom>
                    Parsing Enabled
                  </Typography>
                  <Chip
                    label={documentType.isParsingEnabled ? 'Enabled' : 'Disabled'}
                    color={documentType.isParsingEnabled ? 'success' : 'default'}
                    size='small'
                    variant='filled'
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant='body2' color='text.secondary' gutterBottom>
                    Last Updated
                  </Typography>
                  <Typography variant='body1'>
                    {formatDateString(documentType.updatedAt, 'MMM dd, yyyy HH:mm:ss')}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      {documentType?.documentRules && (
        <TabPanel value={mainTabIndex} index={1} sx={{ padding: '14px 0' }}>
          <Box className='tab-section'>
            <PageHeader
              breadcrumbs={[
                { label: 'Documents', onClick: () => redirect('/documents/library') },
                { label: 'Settings', onClick: () => redirect('/documents/settings') },
                { label: 'Document Types', onClick: () => redirect('/documents/settings') },
                { label: `${documentType.name}`, active: true },
              ]}
            />
          <Card sx={{ boxShadow: 1 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant='h6' gutterBottom sx={{ mb: 3 }}>
                Document Rules Configuration
              </Typography>

              {documentType.documentRules ? (
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Typography variant='subtitle1' gutterBottom color='primary'>
                      Required Fields ({parsedRules.fieldsRequired?.length || 0})
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      {parsedRules.fieldsRequired?.map((field: any, index: number) => (
                        <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant='body2' fontWeight='medium'>
                            {field.name}
                          </Typography>
                          {field.label && (
                            <Typography variant='caption' color='text.secondary'>
                              Label: {field.label}
                            </Typography>
                          )}
                          {field.value && (
                            <Typography variant='caption' color='text.secondary' display='block'>
                              Value: {field.value}
                            </Typography>
                          )}
                        </Box>
                      )) || (
                        <Typography variant='body2' color='text.secondary'>
                          No required fields
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant='subtitle1' gutterBottom color='info.main'>
                      Optional Fields ({parsedRules.fieldsOptional?.length || 0})
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      {parsedRules.fieldsOptional?.map((field: any, index: number) => (
                        <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant='body2' fontWeight='medium'>
                            {field.name}
                          </Typography>
                          {field.label && (
                            <Typography variant='caption' color='text.secondary'>
                              Label: {field.label}
                            </Typography>
                          )}
                        </Box>
                      )) || (
                        <Typography variant='body2' color='text.secondary'>
                          No optional fields
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant='subtitle1' gutterBottom>
                      Raw JSON
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        overflow: 'auto',
                      }}>
                      <pre>{JSON.stringify(parsedRules, null, 2)}</pre>
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <Typography variant='body1' color='text.secondary'>
                  No document rules defined for this document type.
                </Typography>
              )}
            </CardContent>
          </Card>
          </Box>
        </TabPanel>
      )}
    </Box>
  );
};

export default DocumentTypeDetail;
